import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MessageCircle, X, Send, Pizza } from "lucide-react";

const SID_KEY = "rc_chat_sid";
const getSid = () => {
  let s = localStorage.getItem(SID_KEY);
  if (!s) { s = (crypto.randomUUID && crypto.randomUUID()) || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`; localStorage.setItem(SID_KEY, s); }
  return s;
};

const WELCOME = { role: "assistant", text: "Hi! I'm the Regulars Club assistant. Ask me anything about joining a club, member perks, or running your own club." };

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sid = useRef(getSid());
  const endRef = useRef(null);

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat/support", { session_id: sid.current, message: text });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry — I couldn't reach the assistant just now. Please try again in a moment." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-widget-toggle"
          onClick={() => setOpen(true)}
          aria-label="Open support chat"
          className="fixed bottom-5 right-5 z-[80] flex items-center gap-2 px-4 py-3 bg-[#D94A3D] text-white font-semibold rounded-full shadow-[0_8px_24px_rgba(23,50,77,0.25)] hover:bg-[#C33E32] transition-colors"
        >
          <MessageCircle size={20} /> <span className="hidden sm:inline">Need help?</span>
        </button>
      )}

      {open && (
        <div data-testid="chat-widget-panel" className="fixed bottom-5 right-5 z-[80] w-[92vw] max-w-sm bg-white rounded-2xl border border-[#17324D]/10 shadow-[0_16px_48px_rgba(23,50,77,0.2)] flex flex-col overflow-hidden" style={{ height: "min(560px, 80vh)" }}>
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-[#17324D] text-white">
            <div className="flex items-center gap-2">
              <span className="bg-[#D94A3D] text-white p-1.5 rounded-lg"><Pizza size={16} strokeWidth={2.5} /></span>
              <div className="leading-tight">
                <p className="font-heading font-bold text-base">Club Assistant</p>
                <p className="text-[11px] text-white/70">Usually replies instantly</p>
              </div>
            </div>
            <button data-testid="chat-widget-close" onClick={() => setOpen(false)} aria-label="Close chat"><X size={20} /></button>
          </div>

          <div data-testid="chat-messages" className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FFF4E6]">
            {messages.map((m, i) => (
              <div key={i} data-testid={`chat-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] text-sm px-3.5 py-2.5 leading-relaxed ${m.role === "user" ? "bg-[#D94A3D] text-white rounded-2xl rounded-br-md" : "bg-white text-[#17324D] rounded-2xl rounded-bl-md border border-[#17324D]/10"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="text-sm px-3.5 py-2.5 rounded-2xl rounded-bl-md bg-white border border-[#17324D]/10 text-[#17324D]/60">Typing…</div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="p-3 border-t border-[#17324D]/10 flex items-center gap-2 bg-white">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about memberships, perks…"
              className="flex-1 min-w-0 px-3.5 py-2.5 text-sm border border-[#17324D]/20 rounded-full bg-white text-[#17324D] placeholder:text-[#17324D]/40 focus:outline-none focus:ring-2 focus:ring-[#D94A3D]/40 focus:border-[#D94A3D]"
            />
            <button data-testid="chat-send" type="submit" disabled={sending || !input.trim()}
              className="p-2.5 bg-[#D94A3D] text-white rounded-full hover:bg-[#C33E32] transition-colors disabled:opacity-50">
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
